import {Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {WidgetData} from 'features/core/shared/widget/widget-data';
import {WidgetDataParam} from 'features/core/shared/widget/widget-data-param';
import {WidgetFactory} from 'features/core/shared/widget/service/widget.factory';
import {DtoImportObjectContext} from 'features/core/shared/dto/dto.import.object.context';
import {DtoImportObjectContextEntry} from 'features/core/shared/dto/dto.import.object.context.entry';
import {
    WidgetFunctionCallBackCreate,
    WidgetToolbarCreateInterface
} from 'features/core/shared/helper/widget-function-call-back-create';
import {
    buildCpMatrixAgentQuery,
    buildCpWidgetForm,
    CP_MATRIX_AGENT_COLUMNS_FULL,
    CP_MATRIX_AGENT_COLUMNS_SIDE
} from '../../../../../service/domain/cp-widget-column';

/**
 * navItem "Übersicht" of the Provisionsmatrix OBC — the whole content of that OBC, in two widgets.
 *
 * ── WHAT IT SHOWS, AND WHY EXACTLY THIS
 *
 *   1. the matrix HEADER as a DATA widget (`createWidgetEntityData`, `uiComponent: 'data'`), which
 *      renders the entity's own field groups: GENERAL (Name, Alias, Status), RELATION
 *      (Provisionsart, übergeordnete Matrix), PERIOD (Gültig ab/bis), TECH (Id);
 *   2. the matrix ROWS (`cp.ProvisionTypeMatrixAgent`) as a TABLE widget over the shared QL view.
 *
 * The header is a DATA widget on purpose, not `createWidgetObject`: the latter produces
 * `uiComponent: 'object'`, i.e. an EDITING form. Someone who followed an inheritance-chain chip
 * wants to READ what this matrix is, and a form that saves on blur is the wrong thing to hand
 * them. Editing the matrix stays where it was — the config surface of the provision type.
 *
 * ── THE TWO VARIANTS
 *
 * `compact` is bound by the view component and decides three things at once, all of them
 * consequences of the left drawer being narrow and its height belonging to the host:
 *
 *   | | `compact = false` (full) | `compact = true` (side drawer) |
 *   |---|---|---|
 *   | row columns | all twelve (`CP_MATRIX_AGENT_COLUMNS_FULL`) | five (`CP_MATRIX_AGENT_COLUMNS_SIDE`) |
 *   | create affordance | yes, with the matrix pre-filled | NO — design gate FD-6 / S-13 |
 *   | layout | flows with the page | own overflow container, capped height (template) |
 *
 * FD-6 in the gate's words: creating a matrix row out of a five-column lookup drawer, in which its
 * mandatory and band fields are not even visible, is an error path. Suppressing the toolbar button
 * is the same decision the `am-agent-obc` gate took as its FD-7. Nothing else about the drawer is
 * read-only — a row still opens, and the create button is one click away in the full view.
 *
 * The suppression happens in the TEMPLATE, through `[disableUpdate]="compact"` on the row widget,
 * because that is the framework's own switch for it (`mvs-widget.component.ts:313` drops the
 * menu keys `add` / `change` / `show`). It is NOT done by withholding `functionCallbacks` — see
 * the note in {@link refreshRowsWidget}, where that tempting shortcut is measured and rejected.
 *
 * ── ALIASES ARE PER MODE, AND THEY ARE PERMANENT
 *
 * `ui.UiWidget.alias` is a globally unique, DB-backed identity, and the user's column widths and
 * sorting hang off it. The four aliases below must therefore never be renamed, and the two row
 * aliases must stay DIFFERENT from one another (different column sets = different table state) and
 * different from `config.cp.ProvisionTypeMatrixAgent.table`, which the ProvisionType OBC already
 * owns for its own matrix-row table.
 *
 * ── QUERY AND CURATION ARE NOT DEFINED HERE
 *
 * Both come from `service/domain/cp-widget-column.ts` — the module's home for curated table
 * layouts, and now for this query too. A second hand-written copy of either would drift silently:
 * a QL field path that no join produces renders an EMPTY column, without a compile error. The
 * other host of the same table, `cp-provision-type-matrix.component.ts` (navItem "Matrizen" of the
 * ProvisionType OBC), reads the SAME builder and the SAME `CP_MATRIX_AGENT_COLUMNS_FULL`; the two
 * differ only in their query name and widget alias, both of which are identity and must stay apart.
 */
@Component({
    selector: 'cp-provision-type-matrix-overview',
    templateUrl: './cp-provision-type-matrix-overview.component.html',
    styleUrls: ['./cp-provision-type-matrix-overview.component.scss'],
    standalone: false
})
export class CpProvisionTypeMatrixOverviewComponent implements OnInit, OnChanges {

    /** Stable widget aliases, one per uiMode — see the class comment. NEVER rename these. */
    private static readonly cAliasDataFull = 'cp.provisionTypeMatrix.object.data.full';
    private static readonly cAliasDataSide = 'cp.provisionTypeMatrix.object.data.side';
    private static readonly cAliasRowsFull = 'cp.provisionTypeMatrix.object.rows.full';
    private static readonly cAliasRowsSide = 'cp.provisionTypeMatrix.object.rows.side';

    /** QL query names, likewise one per mode, so the two surfaces never share a query identity. */
    private static readonly cQueryRowsFull = 'cp.provisionTypeMatrix.object.rows.full';
    private static readonly cQueryRowsSide = 'cp.provisionTypeMatrix.object.rows.side';

    initialized: boolean;

    @Input() provisionTypeMatrixId: number;

    /** Narrow variant for the left drawer — see the table in the class comment. */
    @Input() compact: boolean = false;

    /** The matrix header — `uiComponent: 'data'`, a reading surface. */
    matrixWidget: WidgetData;

    /** The matrix rows — curated QL table. */
    rowsWidget: WidgetData;

    /**
     * Parent context of both widgets: whatever is opened or created from here belongs to THIS
     * matrix, so the mandatory FK arrives pre-filled and read-only.
     */
    importContext: DtoImportObjectContext;

    ngOnInit(): void {
        this.refreshComponent();
    }

    refreshComponent(): void {

        const matrixId: number = this.provisionTypeMatrixId;

        if (!matrixId) {
            this.matrixWidget = null;
            this.rowsWidget = null;
            this.importContext = null;
            this.initialized = true;
            return;
        }

        this.refreshMatrixWidget(matrixId);
        this.refreshRowsWidget(matrixId);

        this.importContext = new DtoImportObjectContext(
            [new DtoImportObjectContextEntry('cp.ProvisionTypeMatrix', matrixId)]);

        this.initialized = true;
    }

    /**
     * AI:
     * Status: "in progress"
     * Type: Widget
     * SubType: WidgetConfig
     * Reason: Header of ONE provision matrix (cp.ProvisionTypeMatrix) as a data widget — name,
     *     alias, status, the provision type it belongs to, the parent matrix it inherits from and
     *     its validity period. A reading surface: it is what someone who followed an
     *     inheritance-chain chip came to see, and it is deliberately NOT an editing form.
     */
    private refreshMatrixWidget(matrixId: number): void {

        this.matrixWidget = WidgetFactory.createWidgetEntityData(
            this.compact
                ? CpProvisionTypeMatrixOverviewComponent.cAliasDataSide
                : CpProvisionTypeMatrixOverviewComponent.cAliasDataFull,
            'Provisionsmatrix',
            'cp.ProvisionTypeMatrix',
            matrixId,
            WidgetDataParam.create('size', 'S'));
    }

    /**
     * AI:
     * Status: "in progress"
     * Type: Widget
     * SubType: WidgetConfig
     * Reason: Rows of THIS provision matrix (cp.ProvisionTypeMatrixAgent) — the provision amount
     *     per Vermittler or Vermittlergruppe. Curated QL table with explicit joins, because
     *     resolve_fkey_texts cannot reach the Beratername two hops away. Twelve columns in the full
     *     view, five in the narrow drawer. Creating a row is offered in the full view only
     *     (FD-6, enforced by [disableUpdate] in the template): the compact column set hides the
     *     very fields a new row needs.
     */
    private refreshRowsWidget(matrixId: number): void {

        const queryName: string = this.compact
            ? CpProvisionTypeMatrixOverviewComponent.cQueryRowsSide
            : CpProvisionTypeMatrixOverviewComponent.cQueryRowsFull;

        const columns = this.compact ? CP_MATRIX_AGENT_COLUMNS_SIDE : CP_MATRIX_AGENT_COLUMNS_FULL;

        this.rowsWidget = WidgetFactory.createWidgetTableQl(
            this.compact
                ? CpProvisionTypeMatrixOverviewComponent.cAliasRowsSide
                : CpProvisionTypeMatrixOverviewComponent.cAliasRowsFull,
            'Matrixzeilen',
            buildCpMatrixAgentQuery(queryName, matrixId),
            WidgetDataParam.create('size', 'S'),
            WidgetDataParam.create('paging', this.compact ? 10 : 25),
            WidgetDataParam.create('widgetForm', buildCpWidgetForm(columns)));

        /*
         * The create callbacks are set in BOTH modes on purpose — they are the FK PRE-FILL, not
         * the affordance. Whether the "Anlegen" button is offered at all is decided one level up,
         * by `[disableUpdate]="compact"` on the widget (see the template).
         *
         * DO NOT try to suppress the button by leaving `functionCallbacks` unset. Measured in the
         * framework: `WidgetTypeProviderImplementationTable.getMenuButtons()` pushes the "Anlegen"
         * entry UNCONDITIONALLY, and `handleMenuButton` falls back to a bare `new DtoDetail()`
         * when no callbacks exist. Dropping them would therefore keep the button and lose the
         * pre-filled matrix — strictly worse than either intended state.
         */
        const createFields: WidgetToolbarCreateInterface[] = [
            {fieldName: 'provisionTypeMatrixDtoId', fieldValue: matrixId}
        ];
        this.rowsWidget.functionCallbacks = WidgetFunctionCallBackCreate.widgetToolbarCreate(createFields);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
        if (changes['provisionTypeMatrixId'] || changes['compact']) {
            this.refreshComponent();
        }
    }

}
